import { Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MuiMultiFieldWithFocus } from '../../../config/MaterialUIConstants';
import { WORK_ORDER_AUTHORIZATION_TEXT } from '../../../utility/Constant';
import { useGetShopConfig, useUpdateShopConfig } from '../../../services/core/settings';
import SuccessMessageModal from '../../Common/SuccessMessage';

/**
 * Default authorization wording for new Work Orders.
 *
 * Stored on the shop config under `config.workOrderAuthorization.defaultAuthorizationMessage`
 * and saved through PUT shop/update-shop-config with just that block in the body — the endpoint
 * merges, so nothing else on the config is touched.
 *
 * The Work Order editor seeds its Authorization box from this value (spec §4.5); a user can still
 * edit it on an individual Work Order without changing this default.
 */
const WorkOrderAuthorizationText = () => {
    const { data: configData } = useGetShopConfig();
    const updateShopConfigMutation = useUpdateShopConfig();

    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const savedMessage = configData?.config?.workOrderAuthorization?.defaultAuthorizationMessage || '';

    /** Shop name used by the built-in wording, same source as the Work Order editor. */
    const shopName = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('apex-saas_details'))?.shopName || '';
        } catch (e) {
            return '';
        }
    }, []);

    const builtInMessage = useMemo(() => WORK_ORDER_AUTHORIZATION_TEXT(shopName), [shopName]);

    const {
        watch,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({ defaultValues: { defaultAuthorizationMessage: '' } });

    useEffect(() => {
        setValue('defaultAuthorizationMessage', savedMessage);
    }, [savedMessage, setValue]);

    const onSubmit = async (values) => {
        try {
            await updateShopConfigMutation.mutateAsync({
                workOrderAuthorization: {
                    defaultAuthorizationMessage: values.defaultAuthorizationMessage?.trim() || '',
                },
            });
            setSuccessMessage('Changes Saved');
            setOpenSuccessModal(true);
        } catch (error) {
            console.error('Error updating work order authorization text:', error);
        }
    };

    /** Reverts to the value currently stored on the shop config. */
    const handleCancel = () => setValue('defaultAuthorizationMessage', savedMessage);

    /** Puts the built-in default wording back in the box; still needs Save to store it. */
    const handleResetToDefault = () => setValue('defaultAuthorizationMessage', builtInMessage, { shouldDirty: true });

    const isResetDisabled = (watch('defaultAuthorizationMessage') || '') === builtInMessage;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container sx={{ boxShadow: 3, bgcolor: 'white' }} justifyContent="space-between" padding="24px" borderRadius="10px" marginTop="32px">
                <Grid item xs={12} sm={3} md={3} xl={3}>
                    <Typography sx={{ fontSize: '24px', fontWeight: 400, lineHeight: '133.4%' }}>Work Order Authorization Text</Typography>
                    <Typography
                        sx={{
                            mt: '15px',
                            color: '#000',
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                        }}
                    >
                        This text will be used by default on new Work Orders. You can still edit it individually on a specific Work Order.
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={8} md={8} xl={8}>
                    <Typography sx={{ color: '#212121', fontSize: '16px', fontWeight: 500 }}>Default Authorization Text</Typography>

                    <Controller
                        name="defaultAuthorizationMessage"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={5}
                                placeholder="Enter the authorization wording customers will sign on a Work Order."
                                error={!!errors.defaultAuthorizationMessage}
                                helperText={errors.defaultAuthorizationMessage ? errors.defaultAuthorizationMessage.message : ''}
                                sx={{ mt: '8px', ...MuiMultiFieldWithFocus }}
                            />
                        )}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
                        <Typography
                            component="span"
                            onClick={() => {
                                if (!isResetDisabled) {
                                    handleResetToDefault();
                                }
                            }}
                            sx={{
                                color: '#D32F2F',
                                fontSize: '15px',
                                fontWeight: 500,
                                lineHeight: '130%',
                                letterSpacing: '0.46px',
                                textTransform: 'uppercase',
                                cursor: isResetDisabled ? 'default' : 'pointer',
                                opacity: isResetDisabled ? 0.4 : 1,
                            }}
                        >
                            RESET TO DEFAULT
                        </Typography>
                    </Box>

                    <Grid container justifyContent="flex-end" spacing={2} style={{ marginTop: '16px' }}>
                        <Grid item>
                            <Stack spacing={2} direction="row">
                                <Button className="btn-text-size-manage" variant="outlined" color="primary" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    className="btn-text-size-manage"
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={updateShopConfigMutation.isPending}
                                    startIcon={updateShopConfigMutation.isPending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </form>
    );
};

export default WorkOrderAuthorizationText;
